import React, { useEffect, useState } from "react";
import {
  ShieldCheck, Smartphone, KeyRound, AlertCircle, CheckCircle2,
  Copy, Loader2, ShieldOff, Eye, EyeOff, Trash2, DatabaseZap,
} from "lucide-react";
import { authFetch } from "../utils/adminAuth";
import AdminShell from "../components/admin/AdminShell";

// --------------------------------------------------------------------------- //
// Security / 2FA management page                                              //
// --------------------------------------------------------------------------- //

export default function AdminSecurityPage() {
  const [status, setStatus] = useState(null); // { totp_enabled, recovery_codes_remaining }
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { qr_data_url, secret }
  const [enableCode, setEnableCode] = useState("");
  const [enabling, setEnabling] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  // Danger zone: purge all publisher ID documents & fields from the DB
  // + Cloudinary. One-way operation. Confirmed via type-to-confirm.
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  const [toast, setToast] = useState(null);
  const flashToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const resp = await authFetch("/api/admin/2fa/status");
      if (resp.ok) setStatus(await resp.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setSetupData(null);
    setRecoveryCodes(null);
    setEnableCode("");
    const resp = await authFetch("/api/admin/2fa/setup", { method: "POST" });
    if (!resp.ok) {
      flashToast("error", "Failed to start 2FA setup");
      return;
    }
    setSetupData(await resp.json());
  };

  const confirmEnable = async () => {
    if (!/^[0-9]{6}$/.test(enableCode.trim())) {
      flashToast("error", "Enter the 6-digit code shown in your authenticator app.");
      return;
    }
    setEnabling(true);
    try {
      const resp = await authFetch("/api/admin/2fa/enable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: enableCode.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        flashToast("error", data?.detail || "Code did not match.");
        return;
      }
      setRecoveryCodes(data.recovery_codes || []);
      setSetupData(null);
      flashToast("success", "2FA enabled — save your recovery codes!");
      refresh();
    } finally {
      setEnabling(false);
    }
  };

  const confirmDisable = async () => {
    if (!disablePwd) {
      flashToast("error", "Enter your password to confirm.");
      return;
    }
    setDisabling(true);
    try {
      const resp = await authFetch("/api/admin/2fa/disable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: disablePwd, code: disableCode.trim() || null }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        flashToast("error", data?.detail || "Could not disable 2FA.");
        return;
      }
      flashToast("success", "2FA disabled.");
      setDisablePwd("");
      setDisableCode("");
      refresh();
    } finally {
      setDisabling(false);
    }
  };

  // Type-to-confirm gate so admins can't accidentally purge ID data.
  const runIdPurge = async () => {
    const confirmation = window.prompt(
      "This will PERMANENTLY delete every publisher's uploaded ID document and " +
      "all ID-related fields (id_type, id_number, ai verdict, consent records) " +
      "from the database + Cloudinary.\n\n" +
      "Publisher accounts, statuses, and notes are NOT affected.\n\n" +
      "This cannot be undone. Type DELETE ALL IDS to confirm:",
    );
    if (confirmation !== "DELETE ALL IDS") {
      if (confirmation !== null) {
        flashToast("error", "Confirmation text did not match. No action taken.");
      }
      return;
    }
    setPurging(true);
    setPurgeResult(null);
    try {
      const resp = await authFetch("/api/admin/purge-id-data", { method: "POST" });
      const data = await resp.json();
      if (!resp.ok) {
        flashToast("error", data?.detail || "Purge failed.");
        return;
      }
      setPurgeResult(data);
      flashToast(
        "success",
        `Purged: ${data.docs_updated} records cleaned, ${data.cloudinary_deleted} Cloudinary images removed.`,
      );
    } catch (e) {
      flashToast("error", "Purge network error.");
    } finally {
      setPurging(false);
    }
  };

  const copyAll = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    flashToast("success", "Recovery codes copied to clipboard");
  };

  const downloadCodes = () => {
    if (!recoveryCodes) return;
    const blob = new Blob(
      [`DMM Admin – 2FA Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n${recoveryCodes.join("\n")}\n`],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dmm-admin-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminShell pageTitle="Security" pageDescription="Two-factor authentication & login security.">
      {toast && (
        <div
          className={`fixed top-20 right-6 z-[80] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border ${
            toast.kind === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
          data-testid="security-toast"
        >
          {toast.kind === "success" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span className="text-sm font-medium">{toast.msg}</span>
        </div>
      )}

      <div className="max-w-3xl">
        {/* Status card */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${status?.totp_enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {status?.totp_enabled ? <ShieldCheck className="w-6 h-6" /> : <ShieldOff className="w-6 h-6" />}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900">
                {loading ? "Checking…" : status?.totp_enabled ? "2FA is ON" : "2FA is OFF"}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {status?.totp_enabled
                  ? `Logins now require a 6-digit code from your authenticator app. ${status.recovery_codes_remaining} recovery code(s) remaining.`
                  : "Add a second login factor using Google Authenticator, Authy, 1Password, or any TOTP app."}
              </p>
            </div>
          </div>
        </div>

        {/* SETUP (when 2FA OFF and no in-progress setup) */}
        {!status?.totp_enabled && !setupData && !recoveryCodes && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-5 h-5 text-slate-700" />
              <h3 className="text-base font-bold text-slate-900">Enable two-factor authentication</h3>
            </div>
            <ol className="text-sm text-slate-600 space-y-1.5 list-decimal pl-5 mb-5">
              <li>Install an authenticator app (Google Authenticator, Authy, 1Password, Microsoft Authenticator).</li>
              <li>Click <strong>Start setup</strong> below.</li>
              <li>Scan the QR code with your app, then enter the 6-digit code shown.</li>
              <li>Save your <strong>recovery codes</strong> in a safe place.</li>
            </ol>
            <button
              onClick={startSetup}
              data-testid="start-2fa-setup-btn"
              className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 inline-flex items-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" />
              Start setup
            </button>
          </div>
        )}

        {/* SETUP IN PROGRESS — show QR + secret + verify form */}
        {setupData && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <h3 className="text-base font-bold text-slate-900 mb-1">Scan QR code with your authenticator app</h3>
            <p className="text-sm text-slate-500 mb-4">
              Apps: Google Authenticator, Authy, 1Password, Microsoft Authenticator.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 items-start mb-5">
              <img
                src={setupData.qr_data_url}
                alt="2FA QR code"
                width={200}
                height={200}
                className="rounded-md border border-slate-200 bg-white p-2"
                data-testid="2fa-qr-image"
              />
              <div className="flex-1 text-sm">
                <p className="text-slate-700 font-semibold mb-1">Can&rsquo;t scan?</p>
                <p className="text-slate-500 text-xs mb-2">Type this secret into your app manually:</p>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 font-mono text-xs tracking-wider text-slate-800 break-all"
                    data-testid="2fa-secret"
                  >
                    {showSecret ? setupData.secret : "•".repeat(setupData.secret.length)}
                  </code>
                  <button
                    onClick={() => setShowSecret((v) => !v)}
                    className="p-2 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                    title={showSecret ? "Hide secret" : "Show secret"}
                  >
                    {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(setupData.secret); flashToast("success", "Secret copied"); }}
                    className="p-2 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                    title="Copy secret"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1.5 uppercase tracking-[0.14em]">
                Enter the 6-digit code shown in your app
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text" inputMode="numeric" maxLength={6} value={enableCode}
                  onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123 456"
                  className="w-48 text-center text-2xl tracking-[0.4em] font-mono py-2 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                  data-testid="2fa-enable-code-input"
                />
                <button
                  onClick={confirmEnable}
                  disabled={enabling || enableCode.length !== 6}
                  data-testid="2fa-enable-confirm-btn"
                  className="px-5 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {enabling ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Confirm & enable
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RECOVERY CODES — shown ONCE right after enabling */}
        {recoveryCodes && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-amber-900">Save these recovery codes NOW</h3>
                <p className="text-sm text-amber-800 mt-0.5">
                  Each code works <strong>once</strong> and lets you sign in if you lose your phone. They&rsquo;re shown <strong>only this time</strong>.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {recoveryCodes.map((c) => (
                <code key={c} className="px-3 py-2 rounded bg-white border border-amber-200 text-sm font-mono text-amber-900 text-center" data-testid={`recovery-code-${c}`}>
                  {c}
                </code>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={copyAll} data-testid="copy-recovery-codes-btn" className="px-3 py-2 rounded-md bg-amber-700 text-white text-xs font-semibold hover:bg-amber-800 inline-flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy all
              </button>
              <button onClick={downloadCodes} data-testid="download-recovery-codes-btn" className="px-3 py-2 rounded-md bg-white border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-100 inline-flex items-center gap-1.5">
                Download .txt
              </button>
              <button onClick={() => setRecoveryCodes(null)} data-testid="recovery-codes-dismiss-btn" className="ml-auto px-3 py-2 rounded-md text-amber-800 text-xs font-semibold hover:bg-amber-100">
                I&rsquo;ve saved them — dismiss
              </button>
            </div>
          </div>
        )}

        {/* DISABLE (when 2FA is ON) */}
        {status?.totp_enabled && !setupData && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <ShieldOff className="w-5 h-5 text-red-600" />
              <h3 className="text-base font-bold text-slate-900">Disable two-factor authentication</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Provide your password and a current authenticator code to turn 2FA off.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <input
                type="password" value={disablePwd}
                onChange={(e) => setDisablePwd(e.target.value)}
                placeholder="Your admin password"
                className="px-3 py-2 rounded-md border border-slate-200 text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
                data-testid="2fa-disable-password"
              />
              <input
                type="text" inputMode="numeric" maxLength={6} value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Current 6-digit code"
                className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono tracking-[0.2em] focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
                data-testid="2fa-disable-code"
              />
            </div>
            <button
              onClick={confirmDisable}
              disabled={disabling}
              data-testid="2fa-disable-confirm-btn"
              className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {disabling ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Disable 2FA
            </button>
          </div>
        )}
      </div>
      {/* --- Danger Zone: Purge ID Data --- */}
      <div className="mt-8 rounded-xl border border-red-500/40 bg-gradient-to-br from-red-500/10 to-rose-500/5 p-5" data-testid="purge-id-danger-zone">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0 ring-1 ring-red-500/40">
            <DatabaseZap className="w-5 h-5 text-red-300" strokeWidth={2.25} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm">Purge all publisher ID data</h3>
            <p className="text-slate-300 text-xs mt-1 leading-relaxed">
              Since you&apos;ve removed ID collection from the signup form, use this button to permanently delete every publisher&apos;s historical ID document + related fields.
              This wipes uploaded images from Cloudinary and clears <span className="font-mono text-slate-400">id_type, id_number, ai_id_verdict, id_consent</span> and related fields on the MongoDB records.
              Publisher statuses, notes, and everything else stay intact.
            </p>
            <p className="text-red-300 text-xs font-semibold mt-2">
              This is permanent and cannot be undone. Type-to-confirm required.
            </p>

            {purgeResult && (
              <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs" data-testid="purge-result">
                <div className="text-emerald-300 font-semibold mb-1 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Purge complete
                </div>
                <ul className="text-slate-300 space-y-0.5 ml-5 list-disc">
                  <li><span className="font-mono">{purgeResult.docs_scanned}</span> records scanned</li>
                  <li><span className="font-mono">{purgeResult.docs_updated}</span> records had ID fields unset</li>
                  <li><span className="font-mono">{purgeResult.cloudinary_deleted}</span> Cloudinary images deleted{purgeResult.cloudinary_failed ? ` (${purgeResult.cloudinary_failed} failed — check logs)` : ""}</li>
                  <li><span className="font-mono">{purgeResult.local_files_deleted}</span> legacy local files deleted</li>
                </ul>
              </div>
            )}

            <button
              onClick={runIdPurge}
              disabled={purging}
              data-testid="purge-id-btn"
              className="mt-3 inline-flex items-center gap-2 px-4 h-9 rounded-md text-[13px] font-semibold text-white bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 shadow-lg shadow-red-500/20 disabled:opacity-60 transition-all"
            >
              {purging ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.25} /> : <Trash2 className="w-4 h-4" strokeWidth={2.25} />}
              {purging ? "Purging…" : "Purge all ID data"}
            </button>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
